import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFinancialFilters } from '../components/financial/useFinancialFilters';
import { SubTabNav } from '../components/financial/SubTabNav';
import { FinancialFilterBar } from '../components/financial/FinancialFilterBar';
import { GroupByChips } from '../components/financial/GroupByChips';
import {
  FinancialOverviewPanel,
  FinancialDepositsPanel,
  FinancialWithdrawalsPanel,
  FinancialMixPanel,
  FinancialFTDPanel,
  FinancialPlayersPanel,
} from '../components/financial/FinancialPanels';

// Financial Report 2.0 — single page with sub-tabs, shared URL filters.
// Sub-tabs: Overview (NetCash), Deposits, Withdrawals, Mix, FTD, Players.
// All filters live in the URL query string via useFinancialFilters(), so tab
// switching preserves selection and the page is shareable.
export const FinancialReport: React.FC = () => {
  const { t } = useTranslation();
  const filters = useFinancialFilters();

  // Mix is the only tab that currently uses multi-dim chips (payment_method / site).
  // Granularity + payment_type filters only make sense for time/trend panels — we
  // hide payment_type on the Players tab where it has no effect.
  const showGranularity = filters.subTab !== 'players' && filters.subTab !== 'mix';
  const showPaymentType = filters.subTab !== 'players';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('financialReport.title')}</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">{t('financialReport.subtitle')}</p>
      </div>

      <SubTabNav active={filters.subTab} onChange={filters.setSubTab} />

      <FinancialFilterBar filters={filters} showGranularity={showGranularity} showPaymentType={showPaymentType} />

      {filters.subTab === 'mix' && (
        <GroupByChips
          value={filters.dimensions}
          onChange={filters.setDimensions}
          available={['payment_method', 'site']}
        />
      )}

      {filters.subTab === 'overview'    && <FinancialOverviewPanel    filters={filters} />}
      {filters.subTab === 'deposits'    && <FinancialDepositsPanel    filters={filters} />}
      {filters.subTab === 'withdrawals' && <FinancialWithdrawalsPanel filters={filters} />}
      {filters.subTab === 'mix'         && <FinancialMixPanel         filters={filters} />}
      {filters.subTab === 'ftd'         && <FinancialFTDPanel         filters={filters} />}
      {filters.subTab === 'players'     && <FinancialPlayersPanel     filters={filters} />}
    </div>
  );
};
